import { useState, useEffect, useCallback, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MedicationsDispensedContext } from "../MedicationsDispensedContext";
import DashboardService from "../../../../../../services/DashboardService";
import DailyTotalBarChart from "./DailyTotalBarChart/DailyTotalBarChart";
import { ReactComponent as ArrowUpCircle } from "../../../../../../assets/arrow-up-circle.svg";
import { ReactComponent as ArrowDownCircle } from "../../../../../../assets/arrow-down-circle.svg";
import {
  countWeekdaysInRange,
  getNumberOfDaysInDateRange,
} from "../../../../../../functions/dates";
import styles from "./MetricsChartView.module.css";
const MetricsChartViewTrays = ({
  user,
  selectedDayTray,
  onSelectedDayTray,
  selectedDevices,
  selectedSites,
  selectedDateRange,
  applicableDates,
  onApplicableDates,
  startDateCurrentWeek,
  stopDateCurrentWeek,
  startDateLastWeek,
  stopDateLastWeek,
  trayProducts,
  onTrayProducts,
  onTrayProductsSelectedDay,
  trayProductSelectionLookup,
  onTrayProductSelectionLookup,
  onShowDropdowns,
  dailyAverageTrays,
  onDailyAverageTrays,
}) => {
  const { t } = useTranslation("trupakDashboard");

  const {
    readyForServerRequest,
    handleReadyForServerRequest,
    trayProductsAndCountsData,
    handleTrayProductsAndCountsData,
    trayProductsAndCountsDataCurrentWeek,
    handleTrayProductsAndCountsDataCurrentWeek,
    trayProductsAndCountsDataPreviousWeek,
    handleTrayProductsAndCountsDataPreviousWeek,
    batchesCurrentWeek,
    batchesLastWeek,
    batchesSelectedDateRange,
    dispensingOrderIds,
    trayBargraphData,
    handleTrayBargraphData,
  } = useContext(MedicationsDispensedContext);

  const [classNameForBargraph, setClassNameforBargraph] = useState(null);
  const _dashboardServiceRef = useRef(DashboardService);

  useEffect(() => {
    /**
     * Very important
     *  - Needed to clear trayProducts, trayProductSelectionLookup and
     *      dailyAverageTraySelectedDateRange if there are no trays
     **/
    const trayBargraphDataWithValues = trayBargraphData.filter(
      (dataSet) => dataSet.y > 0,
    );

    if (!trayBargraphDataWithValues.length) {
      onTrayProducts([]);
      onTrayProductSelectionLookup({});
      onDailyAverageTrays(0);
    }
  }, [
    trayBargraphData,
    onTrayProducts,
    onTrayProductSelectionLookup,
    onDailyAverageTrays,
  ]);

  /*******************************  MOST FILLED TABLE SELECTED DATE RANGE  ********************************/

  // SELECTED DATE RANGE
  useEffect(() => {
    /**
     * Sets dailyAverageTraySelectedDateRange
     *  - If selectedDayTray is null, this is the average across the entire selected date range
     *  - If selectedDayTray is set, this is the average for that specific day over the date range
     **/
    let tempDailyAverageTraySelectedDateRange;

    if (
      selectedDateRange?.startDate &&
      selectedDateRange?.endDate &&
      !selectedDayTray &&
      selectedDayTray !== 0 &&
      trayProductsAndCountsData.hasOwnProperty("totalTrayCount") &&
      trayProductsAndCountsData.totalTrayCount.length
    ) {
      onDailyAverageTrays(
        (
          trayProductsAndCountsData.totalTrayCount[0].totalTrayCount /
          getNumberOfDaysInDateRange(
            selectedDateRange.startDate,
            selectedDateRange.endDate,
          )
        ).toFixed(2),
      );
    } else if (
      selectedDateRange?.startDate &&
      selectedDateRange?.endDate &&
      applicableDates &&
      (selectedDayTray || selectedDayTray === 0) &&
      trayProductsAndCountsData.hasOwnProperty("totalTrayCount") &&
      trayProductsAndCountsData.totalTrayCount.length &&
      (selectedDayTray || selectedDayTray === 0)
    ) {
      onDailyAverageTrays(
        (
          trayProductsAndCountsData.totalTrayCount[0].totalTrayCount /
          countWeekdaysInRange(
            selectedDateRange?.startDate,
            selectedDateRange?.endDate,
            selectedDayTray,
          )
        ).toFixed(2),
      );
    } else {
      onDailyAverageTrays(0);
    }
  }, [
    selectedDayTray,
    selectedDateRange,
    selectedDayTray,
    applicableDates,
    trayProductsAndCountsData,
    onDailyAverageTrays,
  ]);

  useEffect(() => {
    // Gets graph data for Daily Total Bar chart
    if (
      trayProductsAndCountsData.hasOwnProperty("packageIdTrayCounts") &&
      trayProductsAndCountsData.packageIdTrayCounts.length
    ) {
      const dayOfWeekTrayCounts = trayProductsAndCountsData.dayOfWeekTrayCounts;
      let tempDailyTrayTotalData = [
        { x: "U", y: 0 },
        { x: "M", y: 0 },
        { x: "T", y: 0 },
        { x: "W", y: 0 },
        { x: "R", y: 0 },
        { x: "F", y: 0 },
        { x: "S", y: 0 },
      ];

      for (const dayOfWeekTrayCount of dayOfWeekTrayCounts) {
        switch (dayOfWeekTrayCount.dayName) {
          case "Sunday":
            tempDailyTrayTotalData[0] = {
              x: "U",
              y: dayOfWeekTrayCount?.totalTrayCount,
            };
            break;
          case "Monday":
            tempDailyTrayTotalData[1] = {
              x: "M",
              y: dayOfWeekTrayCount?.totalTrayCount,
            };
            break;
          case "Tuesday":
            tempDailyTrayTotalData[2] = {
              x: "T",
              y: dayOfWeekTrayCount?.totalTrayCount,
            };
            break;
          case "Wednesday":
            tempDailyTrayTotalData[3] = {
              x: "W",
              y: dayOfWeekTrayCount?.totalTrayCount,
            };
            break;
          case "Thursday":
            tempDailyTrayTotalData[4] = {
              x: "R",
              y: dayOfWeekTrayCount?.totalTrayCount,
            };
            break;
          case "Friday":
            tempDailyTrayTotalData[5] = {
              x: "F",
              y: dayOfWeekTrayCount?.totalTrayCount,
            };
            break;
          case "Saturday":
            tempDailyTrayTotalData[6] = {
              x: "S",
              y: dayOfWeekTrayCount?.totalTrayCount,
            };
            break;
          default:
          // code block
        }
      }
      handleTrayBargraphData(tempDailyTrayTotalData);
    }
  }, [trayProductsAndCountsData]);
  /*************************************************  END *************************************************/

  /***********************************  MOST FILLED TABLE SELECTED DAY  ***********************************/
  // SELECTED DAY
  const handleTrayProductsSelectedDay = (day) => {
    /**
     * Sets trayProductsSelectedDay
     * Note that this method also initializes the number of trays each product to 0
     * The tray count for each product is later calculated and updated by ...
     **/
    let trayPackageIds = [];
    let trayProducts_temp = [];
    let dayOfWeek;

    let batchesSelectedDay = batchesSelectedDateRange.filter(
      (batchSelectedDay) => {
        dayOfWeek = new Date(batchSelectedDay.createdAt).getDay();
        return (
          (dayOfWeek === day || (dayOfWeek === 0 && day === 0)) &&
          batchSelectedDay.trayWholeQty > 0
        );
      },
    );

    let dispensingOrderIds = [];
    let tempApplicableDates = [];

    for (const batchSelectedDay of batchesSelectedDay) {
      if (!(batchSelectedDay.createdAt in tempApplicableDates)) {
        tempApplicableDates.push(batchSelectedDay.createdAt);
      }

      if (!dispensingOrderIds.includes(batchSelectedDay.dispensingOrderId)) {
        dispensingOrderIds.push(batchSelectedDay.dispensingOrderId);
      }

      for (const dispensingDetail of batchSelectedDay.dispensingDetails) {
        if (
          !trayPackageIds.includes(dispensingDetail.packageId) &&
          dispensingDetail.isTrayMed
          // && !dispensingDetail.isPartial
        ) {
          trayPackageIds.push(dispensingDetail.packageId);
        }
      }
    }

    let trayProductsSelectedTemp = [];
    trayProducts_temp.sort(compareByQuantityDescending);

    trayProducts.forEach((product) => {
      trayProductsSelectedTemp.push({
        packageId: product.packageId,
        productName: product.productName,
        qty: product.qty,
        partialQty: product.partialQty,
        trays: 0,
        dayOfWeek: product.dayOfWeek,
      });
    });

    trayProductsSelectedTemp = [
      ...trayProductsSelectedTemp.filter((product) => {
        return product.dayOfWeek === day;
      }),
    ];

    if (batchesSelectedDay.length) {
      // convert packageIds array to string list
      let trayPackageIdsList = "";

      trayPackageIds.forEach((packageId) => {
        trayPackageIdsList += `${packageId},`;
      });

      trayPackageIdsList = trayPackageIdsList.substring(
        0,
        trayPackageIdsList.length - 1,
      );

      // convert dispensingOrderIds array to string list
      let trayDispensingOrderIdsList = "";

      dispensingOrderIds.forEach((dispensingOrderId) => {
        trayDispensingOrderIdsList += `${dispensingOrderId},`;
      });

      trayDispensingOrderIdsList = trayDispensingOrderIdsList.substring(
        0,
        trayDispensingOrderIdsList.length - 1,
      );

      tempApplicableDates.sort((a, b) => new Date(a) - new Date(b));
      onApplicableDates(tempApplicableDates);

      let startDateSelectedDay = new Date(tempApplicableDates[0]);
      startDateSelectedDay.setHours(0);
      startDateSelectedDay.setMinutes(0);
      startDateSelectedDay.setSeconds(0);

      let stopDateSelectedDay = new Date(
        tempApplicableDates[tempApplicableDates.length - 1],
      );

      stopDateSelectedDay.setHours(23);
      stopDateSelectedDay.setMinutes(59);
      stopDateSelectedDay.setSeconds(59);

      handleReadyForServerRequest(true);
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const getTrayAndProductCounts1ForSelectedDay = async (
        startDate,
        stopDate,
        packageIds,
        dispensingOrderIds,
      ) => {
        let tempProductTrayCountLookup = {};
        const callback = (data) => {
          for (const result of data.packageIdTrayCounts) {
            if (!(result.packageId in tempProductTrayCountLookup)) {
              tempProductTrayCountLookup[result.packageId] =
                result.totalTrayCount;
            } else {
              tempProductTrayCountLookup[result.packageId] +=
                result.totalTrayCount;
            }
          }
          for (const tempTrayProduct of trayProductsSelectedTemp) {
            if (tempProductTrayCountLookup[tempTrayProduct.packageId]) {
              tempTrayProduct.trays =
                tempProductTrayCountLookup[tempTrayProduct.packageId];
            } else {
              tempTrayProduct.trays = 0;
            }
          }
          const trayProductsSelectedTempWithTrayCounts =
            trayProductsSelectedTemp.map((product) => {
              const matchingResultsObj = data.packageIdTrayCounts.find(
                (result) => result.packageId === product.packageId,
              );
              if (matchingResultsObj) {
                product.trays = matchingResultsObj.totalTrayCount;
              }
              return product;
            });

          onTrayProductsSelectedDay(trayProductsSelectedTempWithTrayCounts);
        };
        getTrayAndProductCounts(
          startDate,
          stopDate,
          packageIds,
          dispensingOrderIds,
          callback,
        );
      };
      getTrayAndProductCounts1ForSelectedDay(
        startDateSelectedDay,
        stopDateSelectedDay,
        trayPackageIdsList,
        trayDispensingOrderIdsList,
      );

      // FOR SELECTED DAY
      // gets tray counts for each product for Most Filled table
      const getTrayAndProductCounts2ForSelectedDay = async (
        startDate,
        stopDate,
        packageIds,
        dispensingOrderIds,
      ) => {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const callback = (data) => handleTrayProductsAndCountsData(data);

        getTrayAndProductCounts(
          startDate,
          stopDate,
          packageIds,
          dispensingOrderIds,
          callback,
        );
      };

      getTrayAndProductCounts2ForSelectedDay(
        startDateSelectedDay,
        stopDateSelectedDay,
        trayPackageIdsList,
        trayDispensingOrderIdsList,
      );
    } else {
      onTrayProductsSelectedDay(trayProductsSelectedTemp);
    }
  };
  /*************************************************  END *************************************************/

  /**************************************  TRAY AND PRODUCTS COUNTS  **************************************/
  const getTrayAndProductCounts = useCallback(
    async function (
      startDate,
      stopDate,
      packageIds,
      dispensingOrderIds,
      callback,
    ) {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      for (let device of selectedDevices) {
        for (let site of selectedSites) {
          await _dashboardServiceRef.current
            .getTruPakTrayAndProductCounts(
              user,
              site,
              device,
              startDate,
              stopDate,
              packageIds,
              dispensingOrderIds,
              timeZone,
              false, // dayOfWeekIsSelected
            )
            .then((results) => {
              if (callback) {
                callback(results.results);
              }
            });
        }
      }
    },
    [
      startDateCurrentWeek,
      stopDateCurrentWeek,
      startDateLastWeek,
      stopDateLastWeek,
      selectedDevices,
      selectedSites,
      user,
    ],
  );

  const getDispensingOrderIds = useCallback(function (batches) {
    let dispensingOrderIds = [];
    let dispensingOrderIdsDates = [];
    let dayOfWeek;
    for (const batch of batches) {
      dayOfWeek = new Date(batch.createdAt).getDay();

      if (
        !dispensingOrderIdsDates.includes(
          `${batches.dispensingOrderId};${dayOfWeek}`,
        )
      ) {
        dispensingOrderIds.push(batch.dispensingOrderId);
        dispensingOrderIdsDates.push(`${batch.dispensingOrderId};${dayOfWeek}`);
      }
    }
    return dispensingOrderIds;
  }, []);

  const getDispensingOrderIdsList = useCallback(
    function (dispensingOrderIds) {
      let dispensingOrderIdsList = "";

      dispensingOrderIds.forEach((dispensingOrderId) => {
        dispensingOrderIdsList += `${dispensingOrderId},`;
      });

      dispensingOrderIdsList = dispensingOrderIdsList.substring(
        0,
        dispensingOrderIdsList.length - 1,
      );
      return dispensingOrderIdsList;
    },
    [dispensingOrderIds],
  );

  const getPackageIdsList = useCallback(function (batches) {
    let trayPackageIds = [];
    let trayPackageIdsDates = [];
    let dayOfWeek;

    for (const batch of batches) {
      dayOfWeek = new Date(batch.createdAt).getDay();
      for (const dispensingDetail of batch.dispensingDetails) {
        // if (dispensingDetail.isTrayMed && !dispensingDetail.isPartial) {
        if (dispensingDetail.isTrayMed) {
          if (
            !trayPackageIdsDates.includes(
              `${dispensingDetail.packageId};${dayOfWeek}`,
            )
          ) {
            trayPackageIds.push(dispensingDetail.packageId);
            trayPackageIdsDates.push(
              `${dispensingDetail.packageId};${dayOfWeek}`,
            );
          }
        }
      }
    }
    return trayPackageIds;
  }, []);

  // FOR BATCHES SELECTED DATE RANGE
  // setTrayProductsAndCountsData(results.results);
  useEffect(() => {
    const callback = (data) => {
      handleTrayProductsAndCountsData(data);
    };

    if (
      !selectedDayTray &&
      !selectedDayTray &&
      selectedDayTray !== 0 &&
      batchesSelectedDateRange.length
    ) {
      const dispensingOrderIds = getDispensingOrderIds(
        batchesSelectedDateRange,
      );

      const dispensingOrderIdsList =
        getDispensingOrderIdsList(dispensingOrderIds);

      const packageIdsList = getPackageIdsList(batchesSelectedDateRange);

      if (
        selectedDateRange.startDate &&
        selectedDateRange.endDate &&
        packageIdsList?.length &&
        dispensingOrderIdsList?.length
      ) {
        getTrayAndProductCounts(
          selectedDateRange.startDate,
          selectedDateRange.endDate,
          packageIdsList,
          dispensingOrderIdsList,
          callback,
        );
      }
    }
  }, [
    getDispensingOrderIdsList,
    getTrayAndProductCounts,
    batchesSelectedDateRange,
    selectedDateRange,
    selectedDayTray,
  ]);

  // FOR BATCHES LAST WEEK
  useEffect(() => {
    const callback = (data) => {
      handleTrayProductsAndCountsDataPreviousWeek(data);
    };
    if (
      readyForServerRequest &&
      !selectedDayTray &&
      selectedDayTray !== 0 &&
      batchesLastWeek.length /** */
    ) {
      const dispensingOrderIds = getDispensingOrderIds(batchesLastWeek /** */);

      const dispensingOrderIdsList =
        getDispensingOrderIdsList(dispensingOrderIds);

      const packageIdsList = getPackageIdsList(batchesLastWeek); /** */

      if (
        startDateLastWeek /** */ &&
        stopDateLastWeek /** */ &&
        packageIdsList?.length &&
        dispensingOrderIdsList?.length
      ) {
        getTrayAndProductCounts(
          startDateLastWeek /** */,
          stopDateLastWeek /** */,
          packageIdsList,
          dispensingOrderIdsList,
          callback,
        );
      }
    }
  }, [
    getDispensingOrderIdsList,
    getTrayAndProductCounts,
    batchesLastWeek,
    startDateLastWeek,
    stopDateLastWeek,
  ]);

  // FOR BATCHES CURRENT WEEK
  useEffect(() => {
    const callback = (data) => {
      handleTrayProductsAndCountsDataCurrentWeek(data);
    };
    if (readyForServerRequest && batchesCurrentWeek.length /** */) {
      const dispensingOrderIds = getDispensingOrderIds(
        batchesCurrentWeek /** */,
      );

      const dispensingOrderIdsList =
        getDispensingOrderIdsList(dispensingOrderIds);

      const packageIdsList = getPackageIdsList(batchesCurrentWeek); /** */
      if (
        startDateCurrentWeek /** */ &&
        stopDateCurrentWeek /** */ &&
        packageIdsList?.length &&
        dispensingOrderIdsList?.length
      ) {
        getTrayAndProductCounts(
          startDateCurrentWeek,
          stopDateCurrentWeek,
          packageIdsList,
          dispensingOrderIdsList,
          callback,
        );
      }
    }
  }, [
    getDispensingOrderIdsList,
    getTrayAndProductCounts,
    batchesCurrentWeek,
    startDateCurrentWeek,
    stopDateCurrentWeek,
  ]);

  /*************************************************  END *************************************************/

  /*******************************  % INCREASE CURRENT WEEK OVER LAST WEEK ********************************/
  let percentIncreaseFromLastWeek = NaN;
  let previousWeekTrayCount = 0;
  let currentWeekTrayCount = 0;

  if (
    trayProductsAndCountsDataPreviousWeek.hasOwnProperty("totalTrayCount") &&
    trayProductsAndCountsDataPreviousWeek.totalTrayCount.length
  ) {
    previousWeekTrayCount =
      trayProductsAndCountsDataPreviousWeek.totalTrayCount[0].totalTrayCount;
  }

  if (
    trayProductsAndCountsDataCurrentWeek.hasOwnProperty("totalTrayCount") &&
    trayProductsAndCountsDataCurrentWeek.totalTrayCount.length
  ) {
    currentWeekTrayCount =
      trayProductsAndCountsDataCurrentWeek.totalTrayCount[0].totalTrayCount;
  }

  if (previousWeekTrayCount > 0) {
    percentIncreaseFromLastWeek =
      ((currentWeekTrayCount - previousWeekTrayCount) / previousWeekTrayCount) *
      100;
  }

  let percentMessage = `${parseFloat(percentIncreaseFromLastWeek).toFixed(
    2,
  )}% ${t("increase from last week")}`;
  let IconIndicator = <ArrowUpCircle />;

  if (isNaN(percentIncreaseFromLastWeek)) {
    percentMessage = "No fills last week";
  } else if (percentIncreaseFromLastWeek < 0) {
    IconIndicator = <ArrowDownCircle />;
    percentMessage = `${(-parseFloat(percentIncreaseFromLastWeek)).toFixed(
      2,
    )}% ${t("decrease from last week")}`;
  }
  /*************************************************  END *************************************************/

  const compareByQuantityDescending = (a, b) => {
    if (a.qty < b.qty) {
      return 1;
    } else if (a.qty > b.qty) {
      return -1;
    } else if (a.productName.toUpperCase() < b.productName.toUpperCase()) {
      return -1;
    }
    return 1;
  };

  const handleClassNameForBargraph = useCallback((name) => {
    setClassNameforBargraph(name);
  }, []);

  const handleNullifyApplicableDates = () => {
    onApplicableDates(null);
  };

  let tableTitle;

  tableTitle = (
    <div className={styles.MetricChartView__bargraphTitle}>
      <h2 className={styles.MetricChartView__bargraphTitleMain}>
        {t("Daily Tray Total")}
      </h2>
    </div>
  );

  return (
    <>
      <div className={styles.MetricChartView__tableContainer}>
        <div className={styles.MetricChartView__bargraphHeadingContainer}>
          <div className={styles.MetricChartView__dailyAverageContainer}>
            <h3>{t("Daily Average")}</h3>
            <p className={styles.MetricChartView_dailyAverage}>
              {dailyAverageTrays}
            </p>
          </div>
          {tableTitle}
          <div
            className={styles.MetricChartView__percentageFromLastWeekContainer}
          >
            {IconIndicator}
            <p>{percentMessage}</p>
          </div>
        </div>

        <DailyTotalBarChart
          user={user}
          stylesName="BarGraph"
          data={trayBargraphData}
          chartWidth="700"
          chartHeight="300"
          onProductsSelectedDay={handleTrayProductsSelectedDay}
          selectedDay={selectedDayTray}
          onSelectedDay={onSelectedDayTray}
          classNameForBargraph={classNameForBargraph}
          onClassNameForBargraph={handleClassNameForBargraph}
          productSelectionLookup={trayProductSelectionLookup}
          onProductSelectionLookup={onTrayProductSelectionLookup}
          onShowDropdowns={onShowDropdowns}
          onNullifyApplicableDates={handleNullifyApplicableDates}
        />
      </div>
    </>
  );
};

export default MetricsChartViewTrays;
